import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

/** 프로그램 테이블 학습내용 열: 글자 수 사다리 대신 셀 가로에 넘칠 때만 축소 */
const MAX_REM = 0.75;
const MIN_REM = 0.52;

function remToPx(rem: number): number {
  const fs = parseFloat(getComputedStyle(document.documentElement).fontSize || '16');
  return rem * (Number.isFinite(fs) ? fs : 16);
}

function clampFontPx(px: number): number {
  const maxPx = remToPx(MAX_REM);
  const minPx = remToPx(MIN_REM);
  return Math.min(maxPx, Math.max(minPx, Math.round(px)));
}

/**
 * preferFirstTryPx: 먼저 시도할 px(이전 프레임 크기).
 * prefer가 있을 때 maxPx를 뒤에 붙이면 안 됨 — 입력이 span보다 좁을 때 과도하게 줄어드는 경우가 생김.
 */
function computeFitFontSizePx(el: HTMLElement, preferFirstTryPx?: number | null): number {
  const maxPx = remToPx(MAX_REM);
  const minPx = remToPx(MIN_REM);
  const clientW = el.clientWidth;
  if (clientW < 2) {
    return preferFirstTryPx != null && preferFirstTryPx > 0 ? clampFontPx(preferFirstTryPx) : maxPx;
  }

  const prevInline = el.style.fontSize;
  const tries: number[] =
    preferFirstTryPx != null && Number.isFinite(preferFirstTryPx) && preferFirstTryPx > 0
      ? [clampFontPx(preferFirstTryPx)]
      : [maxPx];

  for (const tryPx of tries) {
    el.style.fontSize = `${tryPx}px`;
    if (el.scrollWidth <= clientW) {
      el.style.fontSize = prevInline;
      return tryPx;
    }
  }

  const startFrom = tries[0];
  el.style.fontSize = `${startFrom}px`;
  const scrollW = el.scrollWidth;
  el.style.fontSize = prevInline;
  const scaled = Math.floor((startFrom * clientW) / scrollW);
  return Math.max(minPx, scaled);
}

function useFitFontSizePx(measureKey: string) {
  const elRef = useRef<HTMLElement | null>(null);
  const [fontPx, setFontPx] = useState<number>(() => remToPx(MAX_REM));

  const recalc = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    setFontPx((prev) => computeFitFontSizePx(el, prev));
  }, [measureKey]);

  useLayoutEffect(() => {
    recalc();
  }, [recalc]);

  useLayoutEffect(() => {
    const el = elRef.current;
    const td = el?.closest('td');
    if (!td) return;
    let ro: ResizeObserver | null = null;
    const raf = requestAnimationFrame(() => {
      ro = new ResizeObserver(() => recalc());
      ro.observe(td);
    });
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [recalc]);

  return { elRef, fontPx };
}

type FitSpanProps = {
  measureKey: string;
  className: string;
  title?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
};

export function ProgramProgressLearningTextFitSpan({ measureKey, className, title, style, children }: FitSpanProps) {
  const { elRef, fontPx } = useFitFontSizePx(measureKey);
  return (
    <span
      ref={elRef}
      className={className}
      title={title}
      style={{ ...style, fontSize: `${fontPx}px` }}
    >
      {children}
    </span>
  );
}
